import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { reportsApi, projectsApi, usersApi, timeEntriesApi, activitiesApi, dashboardApi, salaryApi, emailApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths, getWeek, getYear, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import {
  FileText, Users, FolderKanban, CheckCircle2, Circle, User, Briefcase,
  Pencil, Trash2, FileBarChart,
} from "lucide-react";
import { cn } from "../lib/utils";

import { TidsposterTab } from "../components/reports/TidsposterTab";
import { EkonomiTab } from "../components/reports/EkonomiTab";
import { LonespecTab } from "../components/reports/LonespecTab";
import { FakturaunderlagTab } from "../components/reports/FakturaunderlagTab";

const Reports = () => {
  const { isAdmin } = useAuth();
  const [report, setReport] = useState(null);
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [profitData, setProfitData] = useState(null);
  const [activeTab, setActiveTab] = useState("tidsposter");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyInvoice, setCompanyInvoice] = useState(null);
  const [companyEmail, setCompanyEmail] = useState("");
  const [ueStart, setUeStart] = useState("");
  const [ueEnd, setUeEnd] = useState("");
  const [allSpecs, setAllSpecs] = useState(null);
  const [specPeriod, setSpecPeriod] = useState("");
  const [filters, setFilters] = useState({
    start_date: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    end_date: format(endOfMonth(new Date()), "yyyy-MM-dd"),
    project_id: "",
    user_id: "",
    status: "godkänd",
    invoiced: "",
    is_ata: "",
  });
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  // Modal states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState("");
  const [detailsEntries, setDetailsEntries] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ hours: "", description: "", project_id: "", activity_id: "", status: "", date: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [collapsedWeeks, setCollapsedWeeks] = useState({});

  useEffect(() => { loadOptions(); loadProfitData(); }, []);
  useEffect(() => { loadReport(); }, [filters]);

  const loadOptions = async () => {
    try {
      const [projectsRes, usersRes, activitiesRes] = await Promise.all([
        projectsApi.getAll(), usersApi.getAll(), activitiesApi.getAll(),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setActivities(activitiesRes.data);
    } catch { /* silent */ }
  };

  const loadProfitData = async () => {
    try { const res = await dashboardApi.getProfit(24); setProfitData(res.data); } catch { /* ignore */ }
  };

  const loadCompanies = async () => {
    try { const res = await salaryApi.getCompanies(); setCompanies(res.data.companies || []); } catch { /* ignore */ }
  };

  const loadCompanyInvoice = async (company, start, end) => {
    try {
      const params = {};
      if (start && end) { params.start = start; params.end = end; }
      const res = await salaryApi.getCompanyInvoice(company, params);
      setCompanyInvoice(res.data);
    } catch { toast.error("Kunde inte ladda underlag"); }
  };

  const loadAllSpecs = async (period) => {
    try {
      const res = await salaryApi.getAll(period || undefined);
      setAllSpecs(res.data);
      if (!specPeriod && res.data.period) setSpecPeriod(res.data.period);
    } catch { /* ignore */ }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const filterData = { ...filters };
      if (!filterData.project_id) delete filterData.project_id;
      if (!filterData.user_id) delete filterData.user_id;
      if (!filterData.status) delete filterData.status;
      if (filterData.invoiced === "") delete filterData.invoiced;
      else filterData.invoiced = filterData.invoiced === "true";
      if (filterData.is_ata === "" || filterData.is_ata === undefined) delete filterData.is_ata;
      else filterData.is_ata = filterData.is_ata === "true" || filterData.is_ata === true;
      const response = await reportsApi.getSummary(filterData);
      setReport(response.data);
      setSelectedEntries([]);
    } catch { toast.error("Kunde inte ladda rapport"); }
    finally { setLoading(false); }
  };

  const setQuickFilter = (type) => {
    const today = new Date();
    let start, end;
    switch(type) {
      case 'week': start = startOfWeek(today, { weekStartsOn: 1 }); end = endOfWeek(today, { weekStartsOn: 1 }); break;
      case 'lastWeek': const lw = new Date(today); lw.setDate(lw.getDate() - 7); start = startOfWeek(lw, { weekStartsOn: 1 }); end = endOfWeek(lw, { weekStartsOn: 1 }); break;
      case 'month': start = startOfMonth(today); end = endOfMonth(today); break;
      case 'lastMonth': const lm = subMonths(today, 1); start = startOfMonth(lm); end = endOfMonth(lm); break;
      default: return;
    }
    setStartDate(start);
    setEndDate(end);
    setFilters(prev => ({ ...prev, start_date: format(start, "yyyy-MM-dd"), end_date: format(end, "yyyy-MM-dd") }));
  };

  const buildFilterData = () => {
    const fd = { ...filters };
    if (!fd.project_id) delete fd.project_id;
    if (!fd.user_id) delete fd.user_id;
    if (!fd.status) delete fd.status;
    if (fd.invoiced === "") delete fd.invoiced; else fd.invoiced = fd.invoiced === "true";
    if (fd.is_ata === "") delete fd.is_ata; else fd.is_ata = fd.is_ata === "true";
    return fd;
  };

  const downloadFile = (data, filename) => {
    const blob = data instanceof Blob ? data : new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.setAttribute("download", filename);
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const fd = buildFilterData();
      if (selectedEntries.length > 0) fd.entry_ids = selectedEntries;
      const res = await reportsApi.exportCsv(fd);
      downloadFile(res.data, `tidsrapport_${format(new Date(), "yyyyMMdd")}.csv`);
      toast.success("CSV exporterad");
    } catch { toast.error("Kunde inte exportera CSV"); }
    finally { setExporting(false); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const fd = buildFilterData();
      if (selectedEntries.length > 0) fd.entry_ids = selectedEntries;
      const res = await reportsApi.exportPdf(fd);
      downloadFile(new Blob([res.data], { type: 'application/pdf' }), `tidsrapport_${format(new Date(), "yyyyMMdd")}.pdf`);
      toast.success("PDF exporterad");
    } catch { toast.error("Kunde inte exportera PDF"); }
    finally { setExporting(false); }
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev => prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]);
  };

  const selectAllInView = (entries) => {
    const ids = entries.map(e => e.id);
    const allSelected = ids.every(id => selectedEntries.includes(id));
    if (allSelected) setSelectedEntries(prev => prev.filter(id => !ids.includes(id)));
    else setSelectedEntries(prev => [...new Set([...prev, ...ids])]);
  };

  const selectNotInvoiced = () => {
    if (!report?.entries) return;
    setSelectedEntries(report.entries.filter(e => !e.invoiced).map(e => e.id));
  };

  const selectedTotals = useMemo(() => {
    if (!report?.entries) return { hours: 0, amount: 0, count: 0 };
    const sel = report.entries.filter(e => selectedEntries.includes(e.id));
    return { hours: sel.reduce((s, e) => s + e.hours, 0), amount: sel.reduce((s, e) => s + (e.hours * (e.hourly_rate || 0)), 0), count: sel.length };
  }, [report, selectedEntries]);

  const handleMarkInvoiced = async (invoiced = true, ids = null) => {
    const entryIds = ids || selectedEntries;
    if (entryIds.length === 0) { toast.error("Valj minst en post"); return; }
    try {
      await timeEntriesApi.markInvoiced(entryIds, invoiced);
      toast.success(invoiced ? "Markerade som fakturerade" : "Avmarkerade");
      setSelectedEntries([]); loadReport();
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const handleToggleInvoiced = async (entryId, e) => {
    if (e) e.stopPropagation();
    try {
      await timeEntriesApi.toggleInvoiced(entryId);
      setReport(prev => {
        if (!prev) return prev;
        return { ...prev, entries: prev.entries.map(entry => entry.id === entryId ? { ...entry, invoiced: !entry.invoiced } : entry) };
      });
    } catch { toast.error("Kunde inte andra fakturastatus"); }
  };

  const handleDeleteEntry = async (entryId, e) => {
    if (e) e.stopPropagation();
    try {
      await timeEntriesApi.delete(entryId);
      setReport(prev => prev ? { ...prev, entries: prev.entries.filter(entry => entry.id !== entryId) } : prev);
      setEditModalOpen(false); setEditingEntry(null);
      toast.success("Tidspost borttagen");
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const handleMarkWeekInvoiced = async (weekEntries, invoiced = true) => {
    try {
      await timeEntriesApi.markInvoiced(weekEntries.map(e => e.id), invoiced);
      toast.success(invoiced ? `${weekEntries.length} poster markerade` : `${weekEntries.length} poster avmarkerade`);
      loadReport();
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const handleMarkProjectInvoiced = async (projectId, invoiced = true) => {
    const ids = (report?.entries || []).filter(e => e.project_id === projectId).map(e => e.id);
    if (ids.length === 0) return;
    try {
      await timeEntriesApi.markInvoiced(ids, invoiced);
      toast.success(invoiced ? `${ids.length} poster markerade` : `${ids.length} poster avmarkerade`);
      loadReport();
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const handleEditEntry = (entry, e) => {
    if (e) e.stopPropagation();
    setEditingEntry(entry);
    setEditForm({ hours: entry.hours.toString(), description: entry.description || "", project_id: entry.project_id, activity_id: entry.activity_id, status: entry.status, date: entry.date });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    setEditSubmitting(true);
    try {
      const data = { hours: parseFloat(editForm.hours), description: editForm.description, project_id: editForm.project_id, activity_id: editForm.activity_id, date: editForm.date };
      if (isAdmin && editForm.status !== editingEntry.status) data.status = editForm.status;
      await timeEntriesApi.update(editingEntry.id, data);
      toast.success("Uppdaterad!");
      setEditModalOpen(false); setEditingEntry(null); loadReport();
    } catch { toast.error("Kunde inte uppdatera"); }
    finally { setEditSubmitting(false); }
  };

  const handleStartDateSelect = (date) => { if (date) { setStartDate(date); setFilters(prev => ({ ...prev, start_date: format(date, "yyyy-MM-dd") })); } };
  const handleEndDateSelect = (date) => { if (date) { setEndDate(date); setFilters(prev => ({ ...prev, end_date: format(date, "yyyy-MM-dd") })); } };

  const weekGroups = useMemo(() => {
    if (!report?.entries) return [];
    const grouped = {};
    report.entries.forEach(entry => {
      const date = parseISO(entry.date);
      const weekNum = getWeek(date, { weekStartsOn: 1 });
      const year = getYear(date);
      const key = `${year}-W${weekNum}`;
      if (!grouped[key]) grouped[key] = { weekNum, year, key, label: `Vecka ${weekNum}`, entries: [], totalHours: 0, totalAmount: 0 };
      grouped[key].entries.push(entry);
      grouped[key].totalHours += entry.hours || 0;
      grouped[key].totalAmount += (entry.hours || 0) * (entry.hourly_rate || 0);
    });
    return Object.values(grouped).sort((a, b) => a.year !== b.year ? b.year - a.year : b.weekNum - a.weekNum);
  }, [report]);

  const invoiceStats = useMemo(() => {
    if (!report?.entries) return { invoicedHours: 0, notInvoicedHours: 0, invoicedAmount: 0, notInvoicedAmount: 0 };
    let ih = 0, nh = 0, ia = 0, na = 0;
    report.entries.forEach(e => {
      const amt = e.hours * (e.hourly_rate || 0);
      if (e.invoiced) { ih += e.hours; ia += amt; } else { nh += e.hours; na += amt; }
    });
    return { invoicedHours: ih, notInvoicedHours: nh, invoicedAmount: ia, notInvoicedAmount: na };
  }, [report]);

  const showDetails = (title, entries) => { setDetailsTitle(title); setDetailsEntries(entries); setDetailsOpen(true); };

  const showProjectDetails = (project) => {
    const projectEntries = report?.entries?.filter(e => e.project_id === project.project_id) || [];
    const projectUsers = report?.by_project_user?.filter(pu => pu.project_id === project.project_id) || [];
    setSelectedProject({ ...project, entries: projectEntries, users: projectUsers });
    setProjectModalOpen(true);
  };

  const toggleWeekCollapse = (key) => { setCollapsedWeeks(prev => ({ ...prev, [key]: !prev[key] })); };

  return (
    <div className="space-y-6 slide-up" data-testid="reports-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FileBarChart className="w-5 h-5 text-[#6dbf4a]" />
          <h1 className="text-2xl font-bold tracking-tight text-white">Rapporter</h1>
        </div>
        <p className="text-white/25 text-sm">Tidsrapporter och fakturaunderlag</p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <button onClick={() => setActiveTab("tidsposter")} data-testid="tab-tidsposter"
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === "tidsposter" ? "bg-[#6dbf4a]/15 text-[#6dbf4a]" : "text-white/30 hover:text-white/60"}`}>
          Tidsposter
        </button>
        <button onClick={() => setActiveTab("ekonomi")} data-testid="tab-ekonomi"
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === "ekonomi" ? "bg-[#6dbf4a]/15 text-[#6dbf4a]" : "text-white/30 hover:text-white/60"}`}>
          Ekonomi
        </button>
        <button onClick={() => { setActiveTab("lonespec"); loadAllSpecs(); }} data-testid="tab-lonespec"
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === "lonespec" ? "bg-[#6dbf4a]/15 text-[#6dbf4a]" : "text-white/30 hover:text-white/60"}`}>
          Lonespec
        </button>
        <button onClick={() => { setActiveTab("ue"); loadCompanies(); }} data-testid="tab-ue"
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === "ue" ? "bg-[#6dbf4a]/15 text-[#6dbf4a]" : "text-white/30 hover:text-white/60"}`}>
          Fakturaunderlag
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "tidsposter" && (
        <TidsposterTab
          isAdmin={isAdmin} report={report} loading={loading} filters={filters} setFilters={setFilters}
          startDate={startDate} endDate={endDate} projects={projects} users={users}
          selectedEntries={selectedEntries} weekGroups={weekGroups} invoiceStats={invoiceStats}
          selectedTotals={selectedTotals} collapsedWeeks={collapsedWeeks} exporting={exporting}
          setQuickFilter={setQuickFilter} handleStartDateSelect={handleStartDateSelect}
          handleEndDateSelect={handleEndDateSelect} handleExportCSV={handleExportCSV}
          handleExportPDF={handleExportPDF} showDetails={showDetails} showProjectDetails={showProjectDetails}
          toggleWeekCollapse={toggleWeekCollapse} handleMarkWeekInvoiced={handleMarkWeekInvoiced}
          handleMarkProjectInvoiced={handleMarkProjectInvoiced} toggleEntrySelection={toggleEntrySelection}
          selectNotInvoiced={selectNotInvoiced} handleMarkInvoiced={handleMarkInvoiced}
          handleToggleInvoiced={handleToggleInvoiced} handleEditEntry={handleEditEntry}
          handleDeleteEntry={handleDeleteEntry} loadReport={loadReport}
        />
      )}

      {activeTab === "ekonomi" && <EkonomiTab profitData={profitData} />}

      {activeTab === "lonespec" && (
        <LonespecTab allSpecs={allSpecs} specPeriod={specPeriod} setSpecPeriod={setSpecPeriod} loadAllSpecs={loadAllSpecs} />
      )}

      {activeTab === "ue" && (
        <FakturaunderlagTab
          companies={companies} selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany}
          companyEmail={companyEmail} setCompanyEmail={setCompanyEmail} companyInvoice={companyInvoice}
          ueStart={ueStart} setUeStart={setUeStart} ueEnd={ueEnd} setUeEnd={setUeEnd}
          loadCompanyInvoice={loadCompanyInvoice}
        />
      )}

      {/* Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">{detailsTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {detailsEntries.length > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4 sticky top-0 bg-[#0d1218] pb-4">
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <p className="text-sm text-white/40">Timmar</p>
                    <p className="text-2xl font-bold text-white">{detailsEntries.reduce((sum, e) => sum + e.hours, 0).toFixed(1)}h</p>
                  </div>
                  {isAdmin && (
                    <div className="bg-[#080c12] rounded-xl p-4">
                      <p className="text-sm text-white/40">Belopp</p>
                      <p className="text-2xl font-bold text-[#6dbf4a]">
                        {detailsEntries.reduce((sum, e) => sum + (e.hours * (e.hourly_rate || 0)), 0).toLocaleString("sv-SE")} kr
                      </p>
                    </div>
                  )}
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <p className="text-sm text-white/40">Poster</p>
                    <p className="text-2xl font-bold text-white">{detailsEntries.length}</p>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={() => selectAllInView(detailsEntries)} className="border-white/[0.06] text-white/40">
                    {detailsEntries.every(e => selectedEntries.includes(e.id)) ? "Avmarkera alla" : "Markera alla"}
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/[0.06]">
                      <TableHead className="w-10"><Checkbox checked={detailsEntries.every(e => selectedEntries.includes(e.id))} onCheckedChange={() => selectAllInView(detailsEntries)} /></TableHead>
                      <TableHead className="text-white/40">Dag</TableHead>
                      <TableHead className="text-white/40">Projekt</TableHead>
                      <TableHead className="text-white/40">Anstalld</TableHead>
                      <TableHead className="text-white/40 text-right">Timmar</TableHead>
                      {isAdmin && <TableHead className="text-white/40 text-right">Belopp</TableHead>}
                      <TableHead className="text-white/40 text-center">Fakt.</TableHead>
                      {isAdmin && <TableHead className="text-white/40 w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailsEntries.sort((a, b) => new Date(b.date) - new Date(a.date)).map(entry => (
                      <TableRow key={entry.id} className={cn("border-white/[0.06]", entry.invoiced && "bg-emerald-500/5")}>
                        <TableCell><Checkbox checked={selectedEntries.includes(entry.id)} onCheckedChange={() => toggleEntrySelection(entry.id)} /></TableCell>
                        <TableCell className="text-white">{format(new Date(entry.date), "EEE d/M", { locale: sv })}</TableCell>
                        <TableCell className="text-white font-medium">{entry.project_name}</TableCell>
                        <TableCell className="text-white/40">{entry.user_name}</TableCell>
                        <TableCell className="text-white text-right font-bold">{entry.hours}h</TableCell>
                        {isAdmin && (
                          <TableCell className="text-[#6dbf4a] text-right">
                            {entry.hourly_rate ? (entry.hours * entry.hourly_rate).toLocaleString("sv-SE") : "-"} kr
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          {isAdmin ? (
                            <button onClick={(e) => handleToggleInvoiced(entry.id, e)} className="hover:scale-110 transition-transform"
                              title={entry.invoiced ? "Avmarkera" : "Markera som fakturerad"}>
                              {entry.invoiced ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /> : <Circle className="w-5 h-5 text-white/40 mx-auto hover:text-emerald-400" />}
                            </button>
                          ) : (
                            entry.invoiced ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /> : <Circle className="w-5 h-5 text-white/25 mx-auto" />
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={(e) => handleEditEntry(entry, e)} className="h-8 w-8 text-white/40 hover:text-white">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <div className="text-center py-12 text-white/40">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Inga poster</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Modal */}
      <Dialog open={projectModalOpen} onOpenChange={setProjectModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#6dbf4a]" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">{selectedProject?.project_name}</DialogTitle>
                <p className="text-white/40">{selectedProject?.client_name}</p>
              </div>
            </div>
          </DialogHeader>
          {selectedProject && (
            <div className="flex-1 overflow-auto space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Totalt</p>
                  <p className="text-2xl font-bold text-white">{selectedProject.hours?.toFixed(1)}h</p>
                </div>
                {isAdmin && (
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <p className="text-sm text-white/40">Belopp</p>
                    <p className="text-2xl font-bold text-[#6dbf4a]">{selectedProject.amount?.toLocaleString("sv-SE")} kr</p>
                  </div>
                )}
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Ej fakturerat</p>
                  <p className="text-2xl font-bold text-amber-400">{selectedProject.entries?.filter(e => !e.invoiced).reduce((s, e) => s + e.hours, 0).toFixed(1)}h</p>
                </div>
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Fakturerat</p>
                  <p className="text-2xl font-bold text-emerald-400">{selectedProject.entries?.filter(e => e.invoiced).reduce((s, e) => s + e.hours, 0).toFixed(1)}h</p>
                </div>
              </div>
              {selectedProject.users?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-[#6dbf4a]" /> Per anstalld</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedProject.users.map(u => (
                      <div key={u.user_id} className="bg-[#080c12] rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#6dbf4a]/20 flex items-center justify-center"><User className="w-4 h-4 text-[#6dbf4a]" /></div>
                          <span className="text-white">{u.user_name}</span>
                        </div>
                        <span className="font-bold text-[#6dbf4a]">{u.hours.toFixed(1)}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-[#6dbf4a]" /> Tidsposter</h3>
                  <Button variant="outline" size="sm" onClick={() => selectAllInView(selectedProject.entries || [])} className="border-white/[0.06] text-white/40">Markera alla</Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/[0.06]">
                      <TableHead className="w-10"><Checkbox /></TableHead>
                      <TableHead className="text-white/40">Datum</TableHead>
                      <TableHead className="text-white/40">Anstalld</TableHead>
                      <TableHead className="text-white/40">Aktivitet</TableHead>
                      <TableHead className="text-white/40 text-right">Timmar</TableHead>
                      {isAdmin && <TableHead className="text-white/40 text-right">Belopp</TableHead>}
                      <TableHead className="text-white/40 text-center">Fakt.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedProject.entries?.map(entry => (
                      <TableRow key={entry.id} className={cn("border-white/[0.06]", entry.invoiced && "bg-emerald-500/5")}>
                        <TableCell><Checkbox checked={selectedEntries.includes(entry.id)} onCheckedChange={() => toggleEntrySelection(entry.id)} /></TableCell>
                        <TableCell className="text-white">{format(new Date(entry.date), "d MMM", { locale: sv })}</TableCell>
                        <TableCell className="text-white/40">{entry.user_name}</TableCell>
                        <TableCell className="text-white/40">{entry.activity_name}</TableCell>
                        <TableCell className="text-white text-right font-medium">{entry.hours}h</TableCell>
                        {isAdmin && (
                          <TableCell className="text-[#6dbf4a] text-right">{entry.hourly_rate ? (entry.hours * entry.hourly_rate).toLocaleString("sv-SE") : "-"} kr</TableCell>
                        )}
                        <TableCell className="text-center">
                          {isAdmin ? (
                            <button onClick={(e) => handleToggleInvoiced(entry.id, e)} className="hover:scale-110 transition-transform">
                              {entry.invoiced ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /> : <Circle className="w-5 h-5 text-white/40 mx-auto hover:text-emerald-400" />}
                            </button>
                          ) : (
                            entry.invoiced ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /> : <Circle className="w-5 h-5 text-white/25 mx-auto" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entry Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06]" data-testid="edit-entry-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#6dbf4a]" /> Redigera tidspost
            </DialogTitle>
            {editingEntry && <p className="text-sm text-white/40 mt-1">{editingEntry.user_name} &bull; {format(new Date(editingEntry.date), "d MMMM yyyy", { locale: sv })}</p>}
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-white/40">Datum</label>
              <Input type="date" value={editForm.date} onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                className="bg-[#080c12] border-white/[0.06] text-white" data-testid="edit-date" />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/40">Projekt</label>
              <Select value={editForm.project_id} onValueChange={(v) => setEditForm(prev => ({ ...prev, project_id: v }))}>
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="edit-project"><SelectValue placeholder="Valj projekt" /></SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                  {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/40">Aktivitet</label>
              <Select value={editForm.activity_id} onValueChange={(v) => setEditForm(prev => ({ ...prev, activity_id: v }))}>
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="edit-activity"><SelectValue placeholder="Valj aktivitet" /></SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                  {activities.map(a => <SelectItem key={a.id} value={a.id} className="text-white">{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="space-y-2">
                <label className="text-sm text-white/40">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                    <SelectItem value="godkänd" className="text-emerald-400">Godkand</SelectItem>
                    <SelectItem value="väntande" className="text-amber-400">Vantande</SelectItem>
                    <SelectItem value="avvisad" className="text-red-400">Avvisad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm text-white/40">Timmar</label>
              <div className="flex gap-2">
                {[0, 1, 2, 4, 8].map(h => (
                  <Button key={h} type="button"
                    variant={editForm.hours === h.toString() ? "default" : "outline"} size="sm"
                    onClick={() => setEditForm(prev => ({ ...prev, hours: h.toString() }))}
                    className={cn("flex-1", editForm.hours === h.toString() ? "bg-[#6dbf4a] text-white" : "bg-[#080c12] border-white/[0.06] text-white")}>
                    {h}h
                  </Button>
                ))}
                <Input type="number" min="0" step="0.25" value={editForm.hours}
                  onChange={(e) => setEditForm(prev => ({ ...prev, hours: e.target.value }))}
                  className="w-20 bg-[#080c12] border-white/[0.06] text-white text-center" data-testid="edit-hours" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/40">Anteckning</label>
              <Input value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Anteckning (valfritt)" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="edit-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="destructive" size="sm"
                onClick={(e) => editingEntry && handleDeleteEntry(editingEntry.id, e)}
                className="mr-auto" data-testid="delete-entry-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Ta bort
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)} className="border-white/[0.06] text-white/40">Avbryt</Button>
              <Button type="submit" disabled={editSubmitting} className="bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="edit-save-btn">
                {editSubmitting ? "Sparar..." : "Spara andringar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
